import { Command as CommandPrimitive } from "cmdk";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import * as React from "react";

import { cn } from "@/shared/lib/cn";
import { POPULAR_REGIONS, regionLabel, type Region } from "@/shared/lib/regions";
import { Button } from "@/shared/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";

interface RegionComboboxProps {
  value: number;
  onChange: (id: number) => void;
}

/**
 * Комбобокс популярных регионов + ввод произвольного числового ID.
 * Подбирает по подстроке в названии или ID.
 */
export function RegionCombobox({ value, onChange }: RegionComboboxProps): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const filtered: Region[] = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return POPULAR_REGIONS;
    return POPULAR_REGIONS.filter(
      (r) => r.label.toLowerCase().includes(q) || String(r.id).includes(q),
    );
  }, [query]);

  const customId = React.useMemo<number | null>(() => {
    const q = query.trim();
    if (!/^\d+$/.test(q)) return null;
    const n = Number(q);
    if (POPULAR_REGIONS.some((r) => r.id === n)) return null;
    return n;
  }, [query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between border-white/15 bg-white/[0.04] text-left font-normal"
        >
          <span className="truncate">{regionLabel(value)}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <CommandPrimitive className="flex h-full w-full flex-col overflow-hidden rounded-md bg-transparent text-popover-foreground">
          <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
            <Search className="h-4 w-4 opacity-50" />
            <CommandPrimitive.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Поиск или ID региона…"
              className="flex h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <CommandPrimitive.List className="max-h-64 overflow-y-auto p-1">
            <CommandPrimitive.Empty className="py-3 text-center text-sm text-muted-foreground">
              Ничего не найдено
            </CommandPrimitive.Empty>
            {filtered.map((r) => (
              <CommandPrimitive.Item
                key={r.id}
                value={`${r.label} ${r.id}`}
                onSelect={() => {
                  onChange(r.id);
                  setOpen(false);
                  setQuery("");
                }}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm",
                  "data-[selected=true]:bg-white/10",
                )}
              >
                <Check
                  className={cn(
                    "h-4 w-4",
                    value === r.id ? "opacity-100" : "opacity-0",
                  )}
                />
                <span className="flex-1 truncate">{r.label}</span>
                <span className="font-mono text-xs text-muted-foreground">{r.id}</span>
              </CommandPrimitive.Item>
            ))}
            {customId !== null && (
              <CommandPrimitive.Item
                value={`custom-${customId}`}
                onSelect={() => {
                  onChange(customId);
                  setOpen(false);
                  setQuery("");
                }}
                className={cn(
                  "mt-1 flex cursor-pointer items-center gap-2 rounded-sm border border-dashed border-purple/40 px-2 py-1.5 text-sm",
                  "data-[selected=true]:bg-white/10",
                )}
              >
                <Check className="h-4 w-4 opacity-0" />
                <span className="flex-1">Использовать ID</span>
                <span className="font-mono text-xs text-purple">{customId}</span>
              </CommandPrimitive.Item>
            )}
          </CommandPrimitive.List>
        </CommandPrimitive>
      </PopoverContent>
    </Popover>
  );
}
